import { Button, DialogContent, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { MerchantInvoiceQRCodePrefix } from '../../utils/merchant-invoices';
import TextFieldWithScan from '../common/textfield-with-scan/textfield-with-scan';
import { Howl } from 'howler';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { IconWithText } from '../common/styles/components';
import { PickupTaskQRCodePrefix } from '../../utils/pickup-tasks';
import { ShelfQRCodePrefix } from '../../utils/shelves';
import { DeliveryBatchQRCodePrefix } from '../../utils/delivery-tasks';
import CustomDialog from '../common/custom-dialog/custom-dialog';
import { isTemporaryPackage } from '../../utils/encoding';

type QuickScanHandler = (id: string, onClose: () => void) => JSX.Element;

const Forbidden: QuickScanHandler = (id, reset) => {
  alert('You are not allowed to view this resource :(');
  reset();
  return <></>;
};

interface QuickScanDialogProps {
  open: boolean;
  onClose: () => void;
  orderHandler?: QuickScanHandler;
  deliveryBatchHandler?: QuickScanHandler;
  pickupTaskHandler?: QuickScanHandler;
  merchantInvoiceHandler?: QuickScanHandler;
  temporaryPackageHandler?: QuickScanHandler;
  shelfHandler?: QuickScanHandler;
}

function QuickScanDialog({
  open,
  onClose,
  orderHandler = Forbidden,
  deliveryBatchHandler = Forbidden,
  pickupTaskHandler = Forbidden,
  merchantInvoiceHandler = Forbidden,
  shelfHandler = Forbidden,
  temporaryPackageHandler = Forbidden,
}: QuickScanDialogProps) {
  const [scannedID, setScannedID] = useState('');
  const [id, setID] = useState('');
  const [viewingOrder, setViewingOrder] = useState<string | null>(null);
  const [viewingDeliveryBatch, setViewingDeliveryBatch] = useState<
    string | null
  >(null);
  const [viewingPickupTask, setViewingPickupTask] = useState<string | null>(
    null
  );
  const [viewingMerchantInvoice, setViewingMerchantInvoice] = useState<
    string | null
  >(null);
  const [viewingShelf, setViewingShelf] = useState<string | null>(null);
  const [viewingTemporaryPackage, setViewingTemporaryPackage] = useState<
    string | null
  >(null);

  const resetStates = () => {
    setID('');
    setScannedID('');
    setViewingDeliveryBatch(null);
    setViewingOrder(null);
    setViewingMerchantInvoice(null);
    setViewingPickupTask(null);
    setViewingShelf(null);
    setViewingTemporaryPackage(null);
  };

  const beepSound = new Howl({
    src: ['/media/Barcode-scanner-beep-sound.mp3'],
    volume: 0.0075,
  });

  useEffect(() => {
    if (id === '') {
      if (scannedID !== '') beepSound.play();
      setID(scannedID);
    }
  }, [scannedID]);

  const handleScan = (val: string) => {
    if (val.length === 0) return;
    if (val.startsWith(PickupTaskQRCodePrefix)) {
      setViewingPickupTask(val.slice(PickupTaskQRCodePrefix.length));
    } else if (val.startsWith(MerchantInvoiceQRCodePrefix)) {
      setViewingMerchantInvoice(val.slice(MerchantInvoiceQRCodePrefix.length));
    } else if (val.startsWith(DeliveryBatchQRCodePrefix)) {
      setViewingDeliveryBatch(val.slice(DeliveryBatchQRCodePrefix.length));
    } else if (val.startsWith('1')) {
      setViewingOrder(val.slice(1));
    } else if (val.startsWith(ShelfQRCodePrefix)) {
      setViewingShelf(val.slice(ShelfQRCodePrefix.length));
    } else if (isTemporaryPackage(val)) {
      setViewingTemporaryPackage(val);
    } else {
      alert('Unexpected QRCode, please try again');
    }
  };

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      fullWidth={true}
      sx={{ borderRadius: '16px' }}
    >
      <DialogContent sx={{ padding: 3 }}>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: 'primary', fontSize: '1.2rem' }}
          >
            <IconWithText centered>
              Quick Scan for Speedy Results!
              <RocketLaunchIcon fontSize="large" color="primary" />
            </IconWithText>
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: '#666', marginTop: '10px' }}
          >
            Scan any QRCode to proceed instantly.
          </Typography>
        </div>
        <TextFieldWithScan
          label="Scan QR Code"
          type="text"
          variant="outlined"
          placeholder="Scan or Enter any QRcode ID"
          fullWidth
          required
          value={''}
          actOnEnterPressed
          act={(value) => handleScan(value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.1)',
            },
          }}
        />
      </DialogContent>

      {viewingDeliveryBatch &&
        deliveryBatchHandler(viewingDeliveryBatch, resetStates)}
      {viewingOrder && orderHandler(viewingOrder, resetStates)}
      {viewingPickupTask && pickupTaskHandler(viewingPickupTask, resetStates)}
      {viewingMerchantInvoice &&
        merchantInvoiceHandler(viewingMerchantInvoice, resetStates)}
      {viewingShelf && shelfHandler(viewingShelf, resetStates)}
      {viewingTemporaryPackage &&
        temporaryPackageHandler(viewingTemporaryPackage, resetStates)}

      <div
        style={{
          width: '100%',
          textAlign: 'center',
          marginTop: '20px',
          marginBottom: '10px',
        }}
      >
        <Button
          size={(isMobile && !isTablet) ? 'small' : 'medium'}
          variant="contained"
          onClick={() => {
            resetStates();
            onClose();
          }}
        >
          Close
        </Button>
      </div>
      <br />
    </CustomDialog>
  );
}

export default QuickScanDialog;
